import { LitElement, html, customElement, property, TemplateResult } from "lit-element";

interface LandingPageTeaser {
  title: string;
  image: string;
  url: string;
}

interface TeaserDataForRender {
  teaser1: { image: string; visibility: "visible" | "non-visible" };
  teaser2: { image: string; visibility: "visible" | "non-visible" };
  url: string;
}

@customElement("landingpage-teaser")
export class LandingPageTeaserElement extends LitElement {
  @property({ type: String, attribute: ":path" }) public path = "";
  @property({ type: Array, attribute: ":categories" }) public categories: string[] = [];
  @property({ type: Number, attribute: ":refresh" }) public refreshInSeconds = 10;
  @property({ type: Boolean, attribute: ":bottom-line" }) public showBottomLine = false;
  @property({ type: Number, attribute: ":show-x-teaser" }) public numberOfShownTeaser = 4;

  @property() public teaser1: LandingPageTeaser[] = [];
  @property() public teaser2: LandingPageTeaser[] = [];
  @property() public allTeaser: LandingPageTeaser[] = [];

  private shownTeaserCount = 0;

  private featuredTeaserNumber(): number {
    return this.shownTeaserCount % 2;
  }

  public constructor() {
    super();
  }

  public createRenderRoot(): LandingPageTeaserElement {
    return this;
  }

  public firstUpdated(): void {
    const endPoint = `/api/teaser/landing-page?path=${encodeURIComponent(this.path)}${this.categories
      .map((c) => `&categories=${c}`)
      .join("")}`;
    fetch(endPoint)
      .then((response) => response.json())
      .then((teaser) => {
        this.allTeaser = teaser;
        this.updateTeaser();
        setInterval(() => {
          this.updateTeaser();
        }, this.refreshInSeconds * 1000);
      });
  }

  private updateTeaser(): void {
    if (this.allTeaser.length <= this.numberOfShownTeaser) {
      return;
    }

    this.shownTeaserCount++;
    const sortAlgorithm = (): number => Math.random() - 0.5;

    const currentlyShownTeaser = this.teaser1.concat(this.teaser2).sort(sortAlgorithm);
    const currentlyNotShownTeaser = this.allTeaser
      .filter((t) => !this.teasersContain(t, currentlyShownTeaser))
      .sort(sortAlgorithm);

    if (this.shownTeaserCount === 1) {
      this.teaser1 = [];
      this.teaser2 = [];

      this.takeNTeaserFrom(
        currentlyNotShownTeaser,
        this.teaser1,
        this.numberOfShownTeaser,
        this.allTeaser.sort(sortAlgorithm)
      );
      this.takeNTeaserFrom(
        currentlyNotShownTeaser,
        this.teaser2,
        this.numberOfShownTeaser,
        this.allTeaser.sort(sortAlgorithm)
      );
    } else if (this.featuredTeaserNumber() == 1) {
      this.teaser1 = [];

      this.takeNTeaserFrom(
        currentlyNotShownTeaser,
        this.teaser1,
        this.numberOfShownTeaser,
        this.allTeaser.sort(sortAlgorithm)
      );
    } else if (this.featuredTeaserNumber() == 0) {
      this.teaser2 = [];

      this.takeNTeaserFrom(
        currentlyNotShownTeaser,
        this.teaser2,
        this.numberOfShownTeaser,
        this.allTeaser.sort(sortAlgorithm)
      );
    }

    this.requestUpdate();
  }

  private takeNTeaserFrom(
    from: LandingPageTeaser[],
    to: LandingPageTeaser[],
    n: number,
    fallbackTeaser: LandingPageTeaser[]
  ): void {
    while (to.length < n && from.length > 0) {
      to.push(from.shift() as LandingPageTeaser);
    }

    if (to.length < n) {
      let i = 0;
      while (to.length < n) {
        to.push(fallbackTeaser[i++]);
      }
    }
  }

  private prepareTeaserForRendering(teaser: LandingPageTeaser, index: number): TeaserDataForRender {
    const url = this.featuredTeaserNumber() == 1 ? teaser.url : this.teaser2[index].url;
    const img1Src = teaser.image;
    const img1Visibility = this.featuredTeaserNumber() == 1 ? "visible" : "non-visible";
    const img2Src = this.teaser2[index]?.image ?? "";
    const img2Visibility = this.featuredTeaserNumber() == 0 ? "visible" : "non-visible";
    return {
      teaser1: { image: img1Src, visibility: img1Visibility },
      teaser2: { image: img2Src, visibility: img2Visibility },
      url: url,
    };
  }

  private renderTeaserData(data: TeaserDataForRender, index: number): TemplateResult {
    return html`
      <div class="col-6 col-lg-3 d-lg-block ${index >= 2 ? "d-none" : ""} image">
        <div class="d-block image-container" aria-hidden="true">
          <img
            src="${data.teaser1.image}"
            class="${data.teaser1.visibility}"
            alt=""
            role="presentation"
            aria-hidden="true"
          />
          <img
            src="${data.teaser2.image}"
            class="${data.teaser2.visibility}"
            alt=""
            role="presentation"
            aria-hidden="true"
          />
        </div>
      </div>
    `;
  }

  protected render(): TemplateResult {
    return html`
      <div class="landingpage-teaser">
        <div class="row images">
          ${this.teaser1
            .map((teaser, index) => this.prepareTeaserForRendering(teaser, index))
            .map((data, index) => this.renderTeaserData(data, index))}
        </div>
        ${this.showBottomLine ? html`<div class="separation-line"></div>` : ""}
      </div>
    `;
  }

  private teasersContain(teaser: LandingPageTeaser, compareTo: LandingPageTeaser[]): boolean {
    return compareTo.some((t) => teaser.title == t.title && teaser.image == t.image && teaser.url == t.url);
  }
}
